<script setup>
    import { ref, computed, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import saveAs from 'file-saver';
    import { http } from '@/shared/httpWrapper.js';
    import { sqlQuery, sqlQueryToExcel, getSavedQueryByGuid } from '@/shared/helpers/api';
    import { getCodeLinkByB64Id } from '@/shared/helpers/routing';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import useShow from '@/shared/composables/useShow.js';
    import _ from 'lodash';
    import { getParameters } from '../sqlEditor/sqlParameterCrawler';
    import FavouriteIcon from '@/shared/components/FavouriteIcon.vue';

    const { showError } = useShow();

    /* DATA */

    const props = defineProps({
        guid: {
            type: String,
            default: null
        }
    });

    const route = useRoute();

    const query = ref();
    const pageTitle = ref();
    const pageSize = ref();
    const sqlParameters = ref();
    const savedQuery = ref();

    const displayQuery = ref(false);
    const loading = ref(false);
    const errorMessage = ref(null);

    // Interactive mode: User can edit parameter values and run query manually
    const modeInteractive = ref(false);

    const result = ref();
    const resultColumns = computed(() => {
        if (result.value && result.value.length) {
            const fieldNames = _.keys(result.value[0]);
            return _.map(fieldNames, fn => ({
                field: fn,
                label: fn,
                sortable: true
            }));
        }
        return [];
    });

    const subTitle = computed(() => {
        if (savedQuery.value) {
            const queryType = savedQuery.value.isPublic
                ? 'Public query'
                : savedQuery.value.isShared
                    ? 'Shared query'
                    : `Private query`;
            return `${queryType} ${savedQuery.value.guid}`;
        } else {
            return null;
        }
    });

    const queryDisplay = computed(() => {
        let builder = query.value;
        for (const name in sqlParameters.value) {
            builder = `SET @${name} = ${sqlParameters.value[name]};\n${builder}`;
        }
        return builder;
    });

    const runRequest = computed(() => ({
        query: query.value,
        arguments: sqlParameters.value
    }));

    /* HELPERS */

    function codeLinkFromDbId(id) {
        return getCodeLinkByB64Id(encodeIdBase64('Code', id));
    }

    /* EVENT HANDLERS */

    function toggleDisplayQuery() {
        displayQuery.value = !displayQuery.value;
    }

    async function runExport() {
        try {
            const response = await sqlQueryToExcel(http, runRequest.value);

            if (response.data) {
                const filename = pageTitle.value + '.xlsx';
                const blob = new Blob([response.data], { type: response.headers['Content-Type'] });
                saveAs(blob, filename);
            }
        } catch (ex) {
            showError(ex);
        }
    }

    async function runQuery() {
        try {
            loading.value = true;
            errorMessage.value = null;
            result.value = (await sqlQuery(http, runRequest.value)).data;
        } catch (ex) {
            showError(ex, m => { errorMessage.value = m; });
        }

        loading.value = false;
    }

    /* INITIALIZATION */

    // If guid is set, load saved query and use this
    async function initByGuid() {
        if (!props.guid)
            return;

        const savedQueryResult = await getSavedQueryByGuid(http, props.guid);

        if (savedQueryResult.queryType !== 1)
            throw Error('The query is not of type SQL');

        savedQuery.value = savedQueryResult;
        query.value = savedQueryResult.spec;
        pageTitle.value = route.query.title || savedQueryResult.name;

        if (savedQueryResult.pageSize && !route.query.pageSize) {
            pageSize.value = savedQueryResult.pageSize;
        }
    }

    async function init() {
        result.value = null;
        errorMessage.value = null;
        modeInteractive.value = false;
        savedQuery.value = null;

        query.value = route.query.query;
        pageTitle.value = route.query.title || 'Query result';
        pageSize.value = parseInt(route.query.pageSize) || 30;
        sqlParameters.value = {};

        await initByGuid();

        if (!query.value) {
            errorMessage.value = 'This page requires a "query" parameter with a SQL expression for library data retrieval.';
            return;
        }

        // Prepare sqlParameters based on expectations from query
        const extractedParameterNames = getParameters(query.value);
        for (const name of extractedParameterNames) {
            sqlParameters.value[name] = '';
        }

        // Fill in parameter values from URL
        for (const name in route.query) {
            if (sqlParameters.value[name] === undefined) {
                // This will skip params like 'query', 'pageSize', and 'title' is not also needed in SQL query
                continue;
            }
            sqlParameters.value[name] = route.query[name];
        }

        // If any parameter is missing a value (empty is considered no value), use interactive mode
        modeInteractive.value = extractedParameterNames.some(pName => sqlParameters.value[pName] === '');

        if (!modeInteractive.value) {
            await runQuery();
        }
    }

    // When the route changes we need to re-initialize
    watch(route, init);

    init();
</script>

<template>
    <div class="columns">
        <div class="column">
            <h1 class="title">
                <b-icon
                    icon="table-search"
                    size="" />
                {{ pageTitle }}
                <favourite-icon />
            </h1>
            <h2
                v-if="subTitle"
                class="subtitle">
                {{ subTitle }}
            </h2>
        </div>
        <div class="column">
            <div
                class="field is-grouped mt-3 is-pulled-right">
                <div class="control buttons has-addons">
                    <b-button
                        icon-left="magnify"
                        :active="displayQuery"
                        @click="toggleDisplayQuery">
                        Show query
                    </b-button>
                    <b-button
                        icon-left="download"
                        @click="runExport">
                        Excel export
                    </b-button>
                </div>
            </div>
        </div>
    </div>

    <b-loading
        v-model="loading"
        is-full-page />

    <b-notification
        v-if="errorMessage"
        type="is-danger"
        has-icon
        role="alert">
        <p>{{ errorMessage }}</p>
    </b-notification>

    <div
        v-show="displayQuery"
        class="block">
        <pre>{{ queryDisplay }}</pre>
    </div>

    <div
        v-show="modeInteractive"
        style="max-width: 50em;"
        class="block">
        <p class="block">
            Please specify parameter value(s) before running the query.
        </p>
        <b-field
            v-for="(_value, name) in sqlParameters"
            :key="name"
            :label="name + ':'">
            <b-input
                v-model="sqlParameters[name]" />
        </b-field>
        <b-button
            icon-left="play"
            type="is-primary"
            @click="runQuery">
            Run query
        </b-button>
    </div>

    <div
        v-if="result"
        class="query-results">
        <b-table
            striped
            narrowed
            paginated
            :per-page="pageSize"
            :data="result">
            <template
                v-for="(column, idx) in resultColumns"
                :key="idx">
                <b-table-column
                    v-slot="tableprops"
                    :field="column.field"
                    :sortable="column.sortable"
                    :searchable="true"
                    :label="column.label">
                    <template v-if="column.field === 'Id' || column.field.endsWith('_ID') && tableprops.row[column.field]">
                        <router-link :to="codeLinkFromDbId(tableprops.row[column.field])">
                            {{ tableprops.row[column.field] }}
                        </router-link>
                    </template>
                    <template v-else-if="tableprops.row[column.field]?.substring && tableprops.row[column.field].substring(0, 8) === 'https://'">
                        <a :href="tableprops.row[column.field]">{{ tableprops.row[column.field] }}</a>
                    </template>
                    <template v-else>
                        <span
                            class="keep-spaces"
                            v-text="tableprops.row[column.field]" />
                    </template>
                </b-table-column>
            </template>
            <template #empty>
                <div class="has-text-left">
                    No records
                </div>
            </template>
            <template #footer>
                <div class="has-text-left has-text-weight-normal is-italic">
                    {{ result.length }} record{{ result.length !== 1 ? 's' : '' }}
                </div>
            </template>
        </b-table>
    </div>
</template>

<style scoped>
.query-results {
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  width: 100%;
  padding: 4px;
}
.keep-spaces {
    white-space: pre;
}
</style>
